import { EWorkshopAndEventPageTabs, PATH_WORKSHOP_AND_EVENTS } from '@configs'
import { zodResolver } from '@hookform/resolvers/zod'
import { message, Tabs } from 'antd'
import { t } from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { workshopEventApi } from 'src/api/workshopEvent'
import { Button, Text } from 'src/common'
import { AddNewForm } from 'src/components/workshopAndEvents/workshopEvent/workshopEventForm/AddNewForm'
import { EventSeo } from 'src/components/workshopAndEvents/workshopEvent/workshopEventForm/EventSeo'
import { IEventDataDetail } from 'src/interfaces/workshopEvent'
import styled from 'styled-components'
import {
  defaultValues,
  IWorkshopEventSchema,
  workshopEventSchema,
} from './schema'

export const WorkshopAndEventFormPage = () => {
  const { eventId } = useParams()
  const [isCreating, setIsCreating] = useState(false)
  const [isGetting, setIsGetting] = useState(false)
  const [workshopEventDetail, setWorkshopEventDetail] =
    useState<IEventDataDetail>()
  const [imageSrc, setImageSrc] = useState('')
  const [imageMobileSrc, setImageMobileSrc] = useState('')
  const navigate = useNavigate()
  // form
  const form = useForm<IWorkshopEventSchema>({
    resolver: zodResolver(workshopEventSchema),
    defaultValues: defaultValues,
    mode: 'onSubmit',
  })
  const items = useFieldArray({ control: form.control, name: 'packageAndShop' })

  const contentError = useMemo(() => {
    return (
      form.formState.errors.backgroundId ||
      form.formState.errors.mobileBackgroundId ||
      form.formState.errors.description ||
      form.formState.errors.eventGuid ||
      form.formState.errors.categoryId ||
      form.formState.errors.subcategoryIds ||
      form.formState.errors.packageAndShop
    )
  }, [
    form.formState.errors.backgroundId,
    form.formState.errors.mobileBackgroundId,
    form.formState.errors.description,
    form.formState.errors.eventGuid,
    form.formState.errors.categoryId,
    form.formState.errors.subcategoryIds,
    form.formState.errors.packageAndShop,
  ])

  const seoError = useMemo(() => {
    return (
      form.formState.errors.canonical ||
      form.formState.errors.metaDescription ||
      form.formState.errors.metaImage ||
      form.formState.errors.schemaMarkup ||
      form.formState.errors.keywords ||
      form.formState.errors.metaTitle
    )
  }, [
    form.formState.errors.canonical,
    form.formState.errors.metaDescription,
    form.formState.errors.metaImage,
    form.formState.errors.schemaMarkup,
    form.formState.errors.keywords,
    form.formState.errors.metaTitle,
  ])

  //tab
  const [activeTab, setActiveTab] = useState<string>(
    EWorkshopAndEventPageTabs.WORKSHOP_AND_EVENT
  )

  const tabItems = [
    {
      label: (
        <Text className={contentError ? '!text-danger' : ''}>
          Workshop & Events
        </Text>
      ),
      key: EWorkshopAndEventPageTabs.WORKSHOP_AND_EVENT,
      children: (
        <AddNewForm
          imageSrc={imageSrc}
          setImageSrc={setImageSrc}
          imageMobileSrc={imageMobileSrc}
          setImageMobileSrc={setImageMobileSrc}
          activeTab={activeTab}
          workshopEventDetail={workshopEventDetail}
          isGettingDetail={isGetting}
          listPackageShops={items}
        />
      ),
    },
    {
      label: (
        <Text className={seoError ? '!text-danger' : ''}>
          {EWorkshopAndEventPageTabs.SEO_CONFIG}
        </Text>
      ),
      key: EWorkshopAndEventPageTabs.SEO_CONFIG,
      children: <EventSeo activeTab={activeTab} />,
    },
  ]

  const onSave = async (data: IWorkshopEventSchema) => {
    try {
      setIsCreating(true)
      const { location, startTime, endTime, ...cleanData } = data
      let res
      if (eventId) {
        res = await workshopEventApi.updateWorkshopEvent(eventId, cleanData)
      } else {
        res = await workshopEventApi.createNew(cleanData)
      }
      if (res.success) {
        navigate(PATH_WORKSHOP_AND_EVENTS)
        message.success({
          content: res?.message ?? t('common:save_success'),
        })
      }
    } catch (error: any) {
      message.error({
        content:
          error?.errors?.toString() ??
          t('error:some_thing_went_wrong_please_try_again'),
      })
    } finally {
      setIsCreating(false)
    }
  }

  const getEventDetail = async (eventId: string) => {
    try {
      setIsGetting(true)
      const res = await workshopEventApi.getWorkshopEventDetail(eventId)
      if (res.success) {
        setWorkshopEventDetail(res.data)
        form.setValue('canonical', res.data.metaData?.canonical || '')
        form.setValue('keywords', res.data.metaData?.keywords || '')
        form.setValue('metaTitle', res.data.metaData?.metaTitle || '')
        form.setValue(
          'metaDescription',
          res.data.metaData?.metaDescription || ''
        )
        form.setValue('metaImage', res.data.metaData?.metaImage || '')
      }
    } catch (error: any) {
      message.error({
        content:
          error?.message ?? t('error:some_thing_went_wrong_please_try_again'),
      })
    } finally {
      setIsGetting(false)
    }
  }

  useEffect(() => {
    if (eventId) {
      getEventDetail(eventId)
    }
  }, [eventId])

  return (
    <WorkshopEventStyle>
      <div className="flex w-full justify-end gap-x-4 mt-5">
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          loading={isCreating}
          type="primary"
          onClick={form.handleSubmit(onSave)}
        >
          Save
        </Button>
      </div>
      <FormProvider {...form}>
        <Tabs
          destroyInactiveTabPane={false}
          defaultActiveKey={EWorkshopAndEventPageTabs.WORKSHOP_AND_EVENT}
          type="card"
          size={'small'}
          items={tabItems}
          defaultChecked={true}
          activeKey={activeTab}
          onChange={(e) => {
            setActiveTab(e)
          }}
        />
      </FormProvider>
    </WorkshopEventStyle>
  )
}
const WorkshopEventStyle = styled('div')(() => {
  return {
    '.ant-tabs-nav': {
      marginBottom: 0,
      paddingLeft: '1.25rem',

      '& .ant-tabs-tab': {
        borderRadius: '0.75rem 0.75rem 0 0 !important',

        '&.ant-tabs-tab-active': {
          span: {
            color: '#009BB7',
            fontWeight: '500',
          },
        },
      },
    },
  }
})
